import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useLocanceGeoCheck, type GeoBlockReason } from '@/hooks/useLocanceGeoCheck';
import { GeoBlockScreen } from '@/components/locance/GeoBlockScreen';
import { GEO_ENABLED } from '@/lib/locance';
import { acquireGeoSession } from '@/lib/acquireGeoSession';
import { mapDepositError } from '@/lib/depositErrors';
import { setPendingJoin } from '@/lib/pendingJoin';
import { useNavigate } from 'react-router-dom';
import { eligibilityToGeoBlock } from '@/lib/geoEligibility';
import {
  UserPlus, Users, Gavel, Clock, DollarSign, Check, Search,
  AlertCircle, Loader2, ChevronLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface LeaguePreview {
  id: string;
  name: string;
  max_teams: number;
  filled_teams: number;
  draft_type: string;
  draft_status: string;
  entry_fee: number;
  commissioner_name: string;
}

interface NFLJoinInlineProps {
  onJoined: (leagueId?: string) => void;
  onBack: () => void;
}

export default function NFLJoinInline({ onJoined, onBack }: NFLJoinInlineProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  // SAB-466 Locance geo gate — FAIL CLOSED for paid buy-ins.
  const geo = useLocanceGeoCheck();
  const [code, setCode] = useState('');
  const [league, setLeague] = useState<LeaguePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [error, setError] = useState<string | null>(null);
  // SAB-493: profile-eligibility block (declared state) → <GeoBlockScreen/>.
  const [eligibilityBlock, setEligibilityBlock] = useState<GeoBlockReason | null>(null);

  const lookupLeague = async () => {
    const normalized = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!normalized || normalized.length < 4) return;

    setLoading(true);
    setError(null);
    setLeague(null);

    try {
      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-join-league?invite_code=${normalized}`
      );
      const data = await res.json();
      if (data.error) {
        setError(data.error === 'League not found' ? 'No league found with that code.' : data.error);
      } else {
        setLeague(data.league);
      }
    } catch {
      setError('Failed to look up league.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user || !league) return;
    setJoining(true);

    // Paid leagues only: profile-eligibility (declared state, fail-OPEN) +
    // Locance geo (fail-CLOSED). Free joins aren't a money moment → no gate.
    let geoSessionId: string | undefined; // SAB-600: forwarded to the server geo gate on a paid join.
    if (league.entry_fee > 0) {
      setEligibilityBlock(null);
      try {
        const { data: session } = await supabase.auth.getSession();
        const eligRes = await fetch(
          `${(supabase as any).supabaseUrl}/functions/v1/check-eligibility`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session?.session?.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'get_user_status' }),
          }
        );
        const eligData = await eligRes.json();
        const block = eligibilityToGeoBlock(eligData);
        if (block) {
          setEligibilityBlock(block);
          setJoining(false);
          return;
        }
      } catch {
        // Fail open if eligibility service is down
      }

      // Locance geo. Gather under GEO_ENABLED OR GEO_OBSERVE (see acquireGeoSession).
      // ok:false only under GEO_ENABLED (enforce client) on a non-Compliant verdict:
      // aborts here and geo.geoBlock drives the existing GeoBlockScreen overlay. Under observe we send
      // the session and let the SERVER decide (no local block, no new UI).
      const geoRes = await acquireGeoSession(geo, league?.id);
      if (!geoRes.ok) {
        setJoining(false);
        return;
      }
      geoSessionId = geoRes.sessionId;
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-join-league`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invite_code: code,
            team_name: teamName.trim() || undefined,
            ...(geoSessionId ? { geo_session_id: geoSessionId } : {}),
          }),
        }
      );

      const data = await res.json();
      if (data.error) {
        if (data.error === 'already_joined') {
          toast({ title: 'Join failed', description: "You're already in this league!", variant: 'destructive' });
          onJoined(data.league_id ?? data.league?.id); // SAB-593: make that league active
        } else if (data.error === 'insufficient_funds' || data.code === 'INSUFFICIENT_FUNDS') {
          // TASK 1b: /nfl/staging joins run through THIS component (NFLStaging renders
          // NFLJoinInline), which had no insufficient branch, so a shortfall showed the
          // raw "Buy-in is $X. You have $Y" toast with no add-funds CTA. Route to the same
          // add-funds-and-join flow the full join page uses (setPendingJoin + deposit).
          const shortfall = Math.max(0, league.entry_fee - Number(data.available ?? 0));
          const deposit = Math.max(shortfall, 10);
          setPendingJoin(code, league.id);
          navigate(`/wallet/deposit?league=${league.id}&amount=${deposit}&join=${encodeURIComponent(code)}&shortfall=${shortfall}`);
        } else {
          // TASK 1: everything else via the shared money-action mapper, so GEO_BLOCKED
          // shows the SAB-605 location copy and KYC/RG map correctly.
          const { title, message } = mapDepositError({ code: data.code, message: data.error, category: data.category, detected_state: data.detected_state });
          toast({ title, description: message, variant: 'destructive' });
        }
      } else {
        toast({ title: 'Joined!', description: `Welcome to ${data.league.name}!` });
        onJoined(data.league?.id);
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* SAB-493 4c geo-block screen — profile-eligibility (declared) OR Locance
          (detected) layer for paid joins. Display-only; server gates authoritative. */}
      {(eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)) && (
        <GeoBlockScreen
          reason={eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)}
          busy={geo.state === 'prompting' || geo.state === 'checking'}
          onRetry={() => handleJoin()}
          onDismiss={() => {
            setEligibilityBlock(null);
            geo.reset();
          }}
        />
      )}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-display font-bold text-foreground">Join League</h1>
      </div>

      {!league && (
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">Enter the invite code from your commissioner</p>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. ABC123"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && lookupLeague()}
                className="font-mono text-lg tracking-wider uppercase bg-muted/30"
                maxLength={8}
              />
              <Button onClick={lookupLeague} disabled={loading || code.length < 4} className="bg-primary hover:bg-primary/90 shrink-0">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {error && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {league && (
        <>
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <h2 className="text-lg font-display font-bold text-foreground">{league.name}</h2>
              <p className="text-xs text-muted-foreground">by {league.commissioner_name}</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded-lg bg-muted/30 border border-border text-center">
                  <Users className="h-3.5 w-3.5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-xs font-bold">{league.filled_teams}/{league.max_teams}</p>
                </div>
                <div className="p-2 rounded-lg bg-muted/30 border border-border text-center">
                  <Gavel className="h-3.5 w-3.5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-xs font-bold">{league.draft_type === 'snake' ? 'Snake' : 'Slow'}</p>
                </div>
                <div className="p-2 rounded-lg bg-muted/30 border border-border text-center">
                  <DollarSign className="h-3.5 w-3.5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-xs font-bold">{league.entry_fee > 0 ? `$${league.entry_fee}` : 'Free'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {league.filled_teams >= league.max_teams ? (
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-foreground font-semibold">League Full</p>
              </CardContent>
            </Card>
          ) : league.draft_status !== 'pending' ? (
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-foreground font-semibold">Draft Already Started</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="p-4 space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-foreground">Your Team Name</label>
                  <Input
                    placeholder="My Team"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    maxLength={30}
                    className="bg-muted/30"
                  />
                </div>
                <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleJoin} disabled={joining}>
                  {joining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Join League
                </Button>
              </CardContent>
            </Card>
          )}

          <button onClick={() => setLeague(null)} className="text-xs text-muted-foreground hover:text-foreground w-full text-center">
            Try a different code
          </button>
        </>
      )}
    </div>
  );
}
